import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styleRuleToIcon } from 'client/pages/MapPage/components/Legend/Legend.utils';
import Tabs from 'client/components/GDS/Tabs/Tabs';
import * as styleRuleEvaluator from 'client/utils/evaluators/styleRuleEvaluator';
import { groupBy, isEmpty, sortBy } from 'lodash';
import getAttributeDisplayTextAndValue from '../featureAttributeHelper';
import styles from '../SelectFeature.module.scss';
import {
    SupplementaryDataTab
} from 'client/pages/MapPage/components/MapInteractions/SelectFeature/SelectFeaturePanel/SupplementaryDataTab/SupplementaryDataTab';
import { getContactSections, supplementaryDataTabAvailable } from 'client/utils/helpers/SupplementaryData/SupplementaryDataUtils';
import {
    TabDisplaySection
} from 'client/pages/MapPage/components/MapInteractions/SelectFeature/SelectFeaturePanel/TabDisplaySection/TabDisplaySection';
import {
    ContactDetailsTab
} from 'client/pages/MapPage/components/MapInteractions/SelectFeature/SelectFeaturePanel/ContactDetailsTab/ContactDetailsTab';
import { selectSectorSpecificStyles } from 'client/store/layersSlice/layersSlice';
import { getDataRefreshStatus } from 'client/utils/helpers/AssetDate/AssetDate';
import DataRefreshStatus from 'client/components/aria/DataRefreshStatus/DataRefreshStatus';
import InfoBox, { getInfoBoxVariant } from 'client/pages/MapPage/components/InfoBox/InfoBox';
import {
    getAssetContacts,
    getAssetOwnerContactsByDataProviderId,
    getAssetOwnerRecordByDataProviderId
} from 'client/utils/helpers/AssetOwner/AssetContactUtils';
import { getRefreshDataFromFeature } from './refreshDataHelper';
import { useScrollToAnchor } from '../RedactedAssetPanel/useScrollToAnchor';
import SelectFeatureAttributeTable from '../SelectFeatureAttributeTable/SelectFeatureAttributeTable';
import { bespokeCodelist, ENHANCED_MEASURES_CODELIST } from 'client/utils/constants/enhancedMeasures';
import { ASSET_TYPES } from 'client/utils/constants/assetTypes';
import {
    ASSET_OWNER_NAME_ATTRIBUTE_NAMES,
    DISPLAY_SECTIONS,
    OWNER_INFORMATION_GROUP_NAME
} from 'client/utils/constants/featureSections';
import { useSupplementaryData } from 'client/utils/hooks/useSupplementaryData';

const SelectFeaturePanel = ({ activeStep }) => {
    const panelRef = useRef();
    const intl = useIntl();

    const assetDetailsTabLabel = intl.formatMessage({ id: 'selectFeaturePanel.tab.asset' });
    const supplementaryDataTabLabel = intl.formatMessage({ id: 'selectFeaturePanel.tab.supplementaryData' });
    const contactDetailsTabLabel = intl.formatMessage({ id: 'selectFeaturePanel.tab.contactDetails' });

    const selected = useSelector(state => state?.map?.selected);
    const openContactDetailsTab = useSelector(state => state?.map?.openContactDetailsTab);
    const supplementaryData = useSelector(state => state?.supplementaryData?.data);
    const assetOwnersData = useSelector(state => state?.assetOwners?.data);
    const mapConfig = useSelector(state => state?.mapConfig);
    const currentLocale = useSelector(state => state?.locale?.current);
    const sectorSpecificStyles = useSelector(selectSectorSpecificStyles);
    const [activeTab, setActiveTab] = useState(assetDetailsTabLabel);
    const ownerDetailsRef = useRef();

    const scrollContactsIntoView = useScrollToAnchor({
        containerRef: panelRef,
        targetRef: ownerDetailsRef,
        offset: -5
    });

    const feature = selected[activeStep - 1];

    const currentAssetTypeId = feature?.getProperties()?.dataproviderassigneduniqueid;
    const assetIdsToFetch = useMemo(() => currentAssetTypeId ? [currentAssetTypeId] : [], [currentAssetTypeId]);
    const {
        loading: supplementaryDataLoading,
        hasError: supplementaryDataError,
        fetch: fetchSuppData,
        retryFailed: retrySuppData
    } = useSupplementaryData(assetIdsToFetch);

    useEffect(() => {
        if (currentAssetTypeId && !supplementaryData?.[currentAssetTypeId]) {
            fetchSuppData();
        }
    }, [currentAssetTypeId]);

    useEffect(() => {
        // Upon changing feature selected, by pressing next or clicking another feature

        // reset to top of panel
        if (panelRef.current) {
            panelRef.current.scrollTop = 0;
            panelRef.current.scrollLeft = 0;
        }

        // Reset to first available tab (realistically asset)
        let firstAvailableTab = assetDetailsTabLabel;
        if (openContactDetailsTab && isContactDetailsTabAvailable) {
            firstAvailableTab = contactDetailsTabLabel;
        } else if (isAssetDetailsTabAvailable) {
            firstAvailableTab = assetDetailsTabLabel;
        } else if (isSupplementaryDataTabAvailable) {
            firstAvailableTab = supplementaryDataTabLabel;
        } else if (isContactDetailsTabAvailable) {
            firstAvailableTab = contactDetailsTabLabel;
        }
        setActiveTab(firstAvailableTab);
    }, [activeStep, selected]);

    if (!feature) {
        return;
    }

    const featureId = feature.getId();
    const assetType = featureId.split('.')[0];
    const featureProps = feature.getProperties();
    const assetTypeId = featureProps.dataproviderassigneduniqueid;
    const layerConfig = mapConfig?.result?.layers?.find(layer => layer?.assetType === assetType);
    const attributesByGroupId = groupBy(layerConfig?.attributes, attribute => attribute?.attributeGroup);
    const groups = groupBy(mapConfig?.result?.attributeGroups, attribute => attribute?.displaySection);

    const layerName = layerConfig?.displayName[currentLocale];

    const enhancedmeasures = Object.values(attributesByGroupId)
        .flat()
        .find(attribute => attribute.attributeName === 'enhancedmeasures');

    const enhancedMeasuresValue = feature?.values_?.enhancedmeasures;
    const featureEnhancedMeasures = { ...enhancedmeasures, value: enhancedMeasuresValue || null };
    const styleRule = styleRuleEvaluator.evaluateStyleRules(feature, layerConfig?.styleRules, sectorSpecificStyles);
    const assetName = styleRule?.displayName[currentLocale];
    const styleId = styleRule?.selectedStyleId || styleRule?.styleId;
    const style = mapConfig?.result?.styles?.find(style => style.id === styleId);
    const icon = style ? styleRuleToIcon(style) : null;

    const supplementalDataProps = supplementaryData?.[assetTypeId];
    const prefixedActorProperties = {};
    if (supplementalDataProps?.nuarActor) {
        Object.entries(supplementalDataProps.nuarActor).forEach(([key, value]) => {
            prefixedActorProperties['NUARACTOR:' + key] = value;
        });
    }

    const getAttributesForGroup = (group) => {
        const attributes = attributesByGroupId[group?.id];
        if (attributes) {
            const attributeValues = attributes
                .sort((a, b) => a?.displayOrder - b?.displayOrder)
                .map(a => a?.isVisible
                    ? getAttributeDisplayTextAndValue(a, { ...featureProps, ...prefixedActorProperties }, supplementalDataProps)
                    : null)
                .filter(v => v != null);
            return {
                ...group,
                attributes: attributeValues
            };
        }
        return null;
    };

    Object.keys(groups).forEach((key) => {
        const groupAttributes = groups[key].map(group => getAttributesForGroup(group)).filter(v => v != null);
        groups[key] = sortBy(groupAttributes, 'displayOrder');
    });

    const featureDetails = {
        layerName,
        assetName,
        enhancedMeasures: featureEnhancedMeasures,
        style,
        icon,
        properties: groups,
        linkedFiles: (Array.isArray(supplementalDataProps?.linkedFiles) ? supplementalDataProps.linkedFiles : [])
            .filter(lf => !Object.values(bespokeCodelist).includes(lf.name)),
        guidances: (Array.isArray(supplementalDataProps?.guidances) ? supplementalDataProps.guidances : [])
            .filter(g => !Object.values(bespokeCodelist).includes(g.name)),
        contacts: supplementalDataProps?.contacts
    };

    const assetSectionProperties = featureDetails.properties[DISPLAY_SECTIONS.ASSET];
    const assetHeaderSection = featureDetails.properties[DISPLAY_SECTIONS.HEADER];
    const ownerSectionProperties = featureDetails.properties[DISPLAY_SECTIONS.OWNER];
    const ownerInformationSectionProperties = (Array.isArray(ownerSectionProperties) ? ownerSectionProperties : [])
        .map(group => ({
            ...group,
            attributes: (group.attributes || []).filter(attribute => !attribute.row)
        }))
        .filter(group => group.attributes.length > 0);
    const informationSectionProperties = sortBy([
        ...(Array.isArray(assetSectionProperties) ? assetSectionProperties : []),
        ...ownerInformationSectionProperties
    ], 'displayOrder');

    if (Array.isArray(informationSectionProperties) && informationSectionProperties.length > 0) {
        const firstObject = informationSectionProperties[0];
        const attributes = firstObject?.attributes;

        if (enhancedMeasuresValue && Object.values(ENHANCED_MEASURES_CODELIST).includes(enhancedMeasuresValue)) {
            const newAttribute = {
                displayName: {
                    cy: '[Special measure]',
                    en: 'Special measure'
                },
                value: enhancedMeasuresValue
            };

            attributes.push(newAttribute);
        }
    }

    const getHeaderSectionsByName = (sectionName) => {
        if (!Array.isArray(assetHeaderSection)) {
            return [];
        }

        const normalisedSectionName = sectionName.toLowerCase();
        return assetHeaderSection.filter(section => {
            const displayNameEn = section?.displayName?.en?.toLowerCase();
            const displayNameCurrentLocale = section?.displayName?.[currentLocale]?.toLowerCase();
            return displayNameEn === normalisedSectionName || displayNameCurrentLocale === normalisedSectionName;
        });
    };

    // Extract asset information and owner information sections from header
    const ownerInformationSection = getHeaderSectionsByName(OWNER_INFORMATION_GROUP_NAME);

    const isSupplementaryDataTabAvailable = supplementaryDataTabAvailable({ featureDetails });
    const isAssetDetailsTabAvailable = !isEmpty(informationSectionProperties);

    // Extract refresh metadata from feature properties
    const { refreshDate, refreshPeriod, refreshUnits } = getRefreshDataFromFeature(featureProps);

    // Get data refresh status for AO status tag
    const dataRefreshStatus = getDataRefreshStatus(refreshDate, refreshPeriod, refreshUnits);

    // Extract Asset Owner name from owner information section (Data provider field)
    let assetOwnerName = null;
    if (Array.isArray(ownerInformationSection) && ownerInformationSection.length > 0) {
        const attributes = ownerInformationSection[0].attributes;
        if (Array.isArray(attributes)) {
            const dataProviderAttr = attributes.find(attr =>
                attr?.displayName && ASSET_OWNER_NAME_ATTRIBUTE_NAMES.includes(attr.displayName.en)
            );
            if (dataProviderAttr) {
                assetOwnerName = dataProviderAttr.value;
            }
        }
    }

    const dataProviderId = featureProps?.dataproviderid_fk || supplementalDataProps?.dataProviderId;
    const assetOwnerRecord = getAssetOwnerRecordByDataProviderId({
        assetOwnersData,
        dataProviderId
    });

    // Fallback to nuarActor displayName if assetOwnerName is not available
    if (!assetOwnerName && supplementalDataProps?.nuarActor?.displayName) {
        assetOwnerName = supplementalDataProps.nuarActor.displayName;
    }

    if (!assetOwnerName && assetOwnerRecord?.label) {
        assetOwnerName = assetOwnerRecord.label;
    }

    // Map enhanced measures value to InfoBox variant
    const infoBoxVariant = getInfoBoxVariant(enhancedMeasuresValue);
    const assetContacts = getAssetContacts({ assetSource: supplementalDataProps, includeGenericContactsKey: true });
    const ownerContacts = getAssetOwnerContactsByDataProviderId({
        assetOwnersData,
        dataProviderId
    });
    const isContactsLoading = supplementaryDataLoading || (!supplementalDataProps && !supplementaryDataError);
    const hasContactDetails = getContactSections({
        supplementaryDataEntries: supplementalDataProps ? [supplementalDataProps] : [],
        assetOwnerName,
        assetContacts,
        ownerContacts,
        supplementaryContactsSource: null
    }).length > 0;
    // Loading and error states still need the tab to show progress or allow a retry
    const isContactDetailsTabAvailable = (assetType !== ASSET_TYPES.OBSERVATION) &&
        (hasContactDetails || isContactsLoading || supplementaryDataError);

    // The contact tab can disappear once loading finishes with no contacts
    const displayedTab = activeTab === contactDetailsTabLabel && !isContactDetailsTabAvailable
        ? (isAssetDetailsTabAvailable ? assetDetailsTabLabel : supplementaryDataTabLabel)
        : activeTab;

    if (!featureDetails || isEmpty(featureDetails.properties) || !selected?.length) {
        return null;
    }

    const scrollToContacts = () => {
        if (isContactDetailsTabAvailable) {
            setActiveTab(contactDetailsTabLabel);
            scrollContactsIntoView();
        }
    };

    const infoBoxAsset = {
        id: assetTypeId,
        dataproviderassigneduniqueid: assetTypeId,
        feature,
        nuarActor: supplementalDataProps?.nuarActor,
        contacts: supplementalDataProps?.contacts
    };
    return <div aria-live='polite' className={[styles.panelContent, 'govuk-width-container'].join(' ')} ref={panelRef}>
        <div className={styles.content}>
            <div className={styles.assetOwnerSection} data-testid='assetOwnerSection'>
                {assetOwnerName && (
                    <div className={['govuk-body-l govuk-!-font-weight-bold', styles.assetOwnerName].join(' ')} data-testid='assetOwnerName'>
                        {assetOwnerName}
                    </div>
                )}
                {dataRefreshStatus && (
                    <DataRefreshStatus
                        isOutOfDate={dataRefreshStatus.isOutOfDate}
                        daysOverdue={dataRefreshStatus.daysOverdue}
                        className='govuk-!-margin-bottom-4 govuk-!-margin-top-4'
                        data-testid='dataRefreshStatus'
                    />
                )}
                {infoBoxVariant && (
                    <InfoBox
                        variant={infoBoxVariant}
                        action={scrollToContacts}
                        asset={infoBoxAsset}
                        assetOwner={assetOwnerName}
                        data-testid='selectFeaturePanelInfoBox'
                    />
                )}
            </div>
            <SelectFeatureAttributeTable data-testid='headerSection'
                sectionProperties={featureDetails.properties[DISPLAY_SECTIONS.HEADER]}
                isExpandedByDefault={true}
            />
        </div>
        <div ref={ownerDetailsRef}>
            <Tabs activeTab={displayedTab} setActiveTab={setActiveTab}>
                {isAssetDetailsTabAvailable &&
                    <TabDisplaySection
                        label={assetDetailsTabLabel}
                        sectionInformation={informationSectionProperties}
                        data-testid={'assetDetailsTab'}
                    />
                }
                {isSupplementaryDataTabAvailable &&
                    <SupplementaryDataTab
                        data-testid='supplementaryDataTab'
                        featureDetails={featureDetails}
                        assetType={assetType}
                        assetTypeId={assetTypeId}
                        label={supplementaryDataTabLabel} />

                }
                {isContactDetailsTabAvailable &&
                    <ContactDetailsTab
                        label={contactDetailsTabLabel}
                        assetOwnerName={assetOwnerName}
                        supplementaryDataEntries={supplementalDataProps ? [supplementalDataProps] : []}
                        assetContacts={assetContacts}
                        ownerContacts={ownerContacts}
                        isContactsLoading={isContactsLoading}
                        hasContactsError={supplementaryDataError}
                        onRetryContacts={() => retrySuppData(assetIdsToFetch)}
                    />
                }
            </Tabs>
        </div>
    </div>;
};

SelectFeaturePanel.propTypes = {
    activeStep: PropTypes.number.isRequired
};

export default (SelectFeaturePanel);
